import { useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTemplates } from '@/hooks/useTemplates';
import { useCrmMeta } from '@/hooks/useCrm';
import ValidatedInput from '@/components/ValidatedInput';
import DateInput from '@/components/DateInput';

const EDUCATION_FALLBACK = ['10th', '12th', 'Diploma', 'Graduate', 'Post-Graduate', 'PhD'];

const calculateAge = (dob) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age -= 1;
  return age >= 0 ? String(age) : '';
};

const Field = ({ label, children, span }) => (
  <div className={span ? `md:col-span-${span} space-y-1.5` : 'space-y-1.5'}>
    <Label className="text-xs font-medium text-gray-600  tracking-wider">{label}</Label>
    {children}
  </div>
);

/**
 * Reusable person-record form (MD, Director, Managing Partner, Partner, Proprietor).
 * - `dinLabel` lets us swap DIN ↔ DPIN.
 * - `showDin`, `showYearOfGraduation` toggle optional fields.
 * - `testidPrefix` keeps Playwright stable.
 */
const PersonForm = ({
  value,
  onChange,
  dinLabel = 'DIN',
  showDin = true,
  showYearOfGraduation = false,
  testidPrefix,
}) => {
  const tpl = useTemplates();
  const meta = useCrmMeta();
  const patterns = meta?.validation_patterns || {};
  const educationOptions = meta?.education_options || EDUCATION_FALLBACK;
  // Prefer CRM meta (accessible to all auth'd users) and fall back to /api/templates
  // (only super_admin / doc-team-with-templates feature can reach it).
  const indianStates = meta?.indian_states?.length ? meta.indian_states : tpl.indianStates;
  const districtsByState = (meta?.districts_by_state && Object.keys(meta.districts_by_state).length)
    ? meta.districts_by_state
    : tpl.districtsByState;
  const districts = (value.state && districtsByState?.[value.state]) || [];
  const hasDistrictList = districts.length > 0;

  const set = useCallback((k, v) => {
    onChange({ ...value, [k]: v });
  }, [onChange, value]);

  // Auto-calc age whenever DoB changes
  useEffect(() => {
    if (!value.date_of_birth) return;
    const age = calculateAge(value.date_of_birth);
    if (age !== value.age) onChange({ ...value, age });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.date_of_birth]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Field label="Name">
        <Input data-testid={`${testidPrefix}-name`} value={value.name || ''} onChange={(e) => set('name', e.target.value)} />
      </Field>
      <Field label="Address" span={2}>
        <Input data-testid={`${testidPrefix}-address`} value={value.address || ''} onChange={(e) => set('address', e.target.value)} />
      </Field>

      <Field label="Date of Birth">
        <DateInput value={value.date_of_birth || ''} onChange={(v) => set('date_of_birth', v)} testid={`${testidPrefix}-dob`} />
      </Field>
      <Field label="Age (auto)">
        <Input data-testid={`${testidPrefix}-age`} value={value.age || ''} readOnly className="bg-gray-50" />
      </Field>
      {showDin ? (
        <ValidatedInput
          label={dinLabel}
          patterns={patterns}
          patternKey="din"
          value={value.din}
          onChange={(v) => set('din', v)}
          testid={`${testidPrefix}-din`}
        />
      ) : <div />}

      <Field label="State">
        <Select value={value.state || 'Kerala'} onValueChange={(v) => onChange({ ...value, state: v, district: '' })}>
          <SelectTrigger data-testid={`${testidPrefix}-state`}><SelectValue /></SelectTrigger>
          <SelectContent>{(indianStates || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="District">
        {hasDistrictList ? (
          <Select value={value.district || ''} onValueChange={(v) => set('district', v)}>
            <SelectTrigger data-testid={`${testidPrefix}-district`}><SelectValue placeholder="Select district" /></SelectTrigger>
            <SelectContent>{districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Input
            data-testid={`${testidPrefix}-district`}
            placeholder="Enter district"
            value={value.district || ''}
            onChange={(e) => set('district', e.target.value)}
          />
        )}
      </Field>
      <ValidatedInput
        label="Aadhar"
        patterns={patterns}
        patternKey="aadhar"
        value={value.aadhar}
        onChange={(v) => set('aadhar', v)}
        testid={`${testidPrefix}-aadhar`}
      />

      <ValidatedInput
        label="PAN"
        patterns={patterns}
        patternKey="pan"
        value={value.pan}
        onChange={(v) => set('pan', v)}
        testid={`${testidPrefix}-pan`}
      />
      <Field label="Education">
        <Select value={value.education || ''} onValueChange={(v) => set('education', v)}>
          <SelectTrigger data-testid={`${testidPrefix}-education`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {educationOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="College">
        <Input data-testid={`${testidPrefix}-college`} value={value.college || ''} onChange={(e) => set('college', e.target.value)} />
      </Field>

      {showYearOfGraduation && (
        <Field label="Year of Graduation">
          <Input data-testid={`${testidPrefix}-yog`} value={value.year_of_graduation || ''} onChange={(e) => set('year_of_graduation', e.target.value)} placeholder="e.g. 2010" />
        </Field>
      )}

      <Field label="Police Station">
        <Input
          data-testid={`${testidPrefix}-police-station`}
          value={value.police_station || ''}
          onChange={(e) => set('police_station', e.target.value)}
          placeholder="Police station name"
        />
      </Field>
      <Field label="Police Station Address" span={2}>
        <Input
          data-testid={`${testidPrefix}-police-station-address`}
          value={value.police_station_address || ''}
          onChange={(e) => set('police_station_address', e.target.value)}
        />
      </Field>
    </div>
  );
};

// Unique client-side key for stable React lists.
const newKey = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `k_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const emptyPerson = () => ({
  _key: newKey(),
  name: '', address: '', date_of_birth: '', age: '', din: '',
  state: 'Kerala', district: '', aadhar: '', pan: '',
  education: '', college: '', year_of_graduation: '',
  police_station: '', police_station_address: '',
});

// Backfill a `_key` on a person dict if missing (e.g. record loaded from API).
export const withKey = (person) =>
  person && !person._key ? { ...person, _key: newKey() } : person;

export default PersonForm;
